import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Clock, PlayCircle } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import { Button } from "./ui/button";

const VideoBlogLayout = ({
  title,
  category = "Video Demo",
  readTime,
  date,
  author,
  eyebrow,
  subtitle,
  videoUrl,
  videoTitle,
  videoProvider = "loom",
  children,
}) => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="pt-32 pb-12 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-50 via-white to-white" />

        <div className="container mx-auto max-w-4xl relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-sm font-medium border border-blue-100 mb-8"
          >
            <PlayCircle className="w-4 h-4" />
            <span>{category}</span>
          </motion.div>

          {eyebrow && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.05 }}
              className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600 mb-3"
            >
              {eyebrow}
            </motion.div>
          )}

          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="text-4xl md:text-6xl font-bold mb-6 text-gray-900 leading-[1.05] tracking-tight"
          >
            {title}
          </motion.h1>

          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.18 }}
              className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed mb-8"
            >
              {subtitle}
            </motion.p>
          )}

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.28 }}
            className="flex items-center justify-center space-x-4 text-sm text-gray-500 font-medium"
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 text-xs font-bold">
                {author?.[0] || "A"}
              </div>
              <span>{author || "Asha Team"}</span>
            </div>
            {date && (
              <>
                <span>•</span>
                <span>{date}</span>
              </>
            )}
            {readTime && (
              <>
                <span>•</span>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{readTime}</span>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </section>

      <section className="pb-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="rounded-2xl md:rounded-3xl overflow-hidden border border-gray-200 shadow-xl bg-black"
          >
            <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
              <iframe
                src={videoUrl}
                title={videoTitle || title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full"
              />
            </div>
          </motion.div>
          {videoProvider && (
            <p className="text-center text-xs text-gray-400 mt-3">
              Video hosted on {videoProvider.charAt(0).toUpperCase() + videoProvider.slice(1)}
            </p>
          )}
        </div>
      </section>

      {children && (
        <article className="pb-24 px-6">
          <div className="container mx-auto max-w-3xl">{children}</div>
        </article>
      )}

      <section className="py-20 px-6 bg-gray-900 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-8">
            Ready to Automate Your Practice?
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto">
            Join the only platform that offers a complete team of AI agents to drive your practice's growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://calendly.com/asha-sales/demo" target="_blank" rel="noopener noreferrer">
              <Button className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-6 rounded-xl text-lg font-bold shadow-lg transition-all duration-300 w-full sm:w-auto">
                Schedule Follow-up Call
              </Button>
            </a>
            <a href="https://calendly.com/asha-sales/demo" target="_blank" rel="noopener noreferrer">
              <Button className="bg-blue-600 hover:bg-blue-500 text-white border border-blue-500 px-8 py-6 rounded-xl text-lg font-bold shadow-lg transition-all duration-300 w-full sm:w-auto">
                Book a Demo
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VideoBlogLayout;
